import { useEffect, useState } from 'react';
import { Badge, Box, Button, Card, Flex, Icon, Spinner, Stack, Text } from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { CurriculumPlan } from '@/lib/types';
import { draftFromSlot } from '@/pages/planner/draft';
import { PlannerAction } from '@/pages/planner/planner-machine';
import useCurriculumsStore from '@/utils/use-curriculums';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { toaster } from '@/components/ui/toaster';
import CurDetailsModal from './cur-details-modal';

interface CurBuildScreenProps {
  curriculumId: number;
  dispatch: (action: PlannerAction) => void;
}

// "curBuild" (admin) — a plain slot list for one curriculum: "x of y plans
// complete" in the header, then a row per slot with a COMPLETE / INCOMPLETE
// chip and its actions (View + Edit once complete, Add content while empty).
// A slot is complete once anything is authored into it — goals, notes, or a
// content item (is_built is derived server-side; see planner service).
// "Edit details" opens the curriculum's own name/description/outcomes dialog —
// the only place those are editable after creation.
export default function CurBuildScreen({ curriculumId, dispatch }: CurBuildScreenProps) {
  const items = useCurriculumsStore((s) => s.items);
  const fetchOne = useCurriculumsStore((s) => s.fetchOne);
  const [isLoading, setIsLoading] = useState(true);
  const detailsModal = useModalToggle();

  const curriculum = items.find((c) => c.id === curriculumId);

  useEffect(() => {
    setIsLoading(true);
    fetchOne(curriculumId)
      .catch((error: any) => {
        toaster.create({
          type: 'error',
          title: error.response?.data?.error ?? 'Failed to load curriculum',
        });
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculumId]);

  if (isLoading && !curriculum) {
    return (
      <Flex justify='center' py={10}>
        <Spinner color='fg.subtle' />
      </Flex>
    );
  }

  if (!curriculum) {
    return (
      <Text fontSize='sm' color='fg.muted'>
        Curriculum not found.
      </Text>
    );
  }

  const slots = [...(curriculum.plans ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const builtCount = curriculum.built_count;
  const planCount = curriculum.plan_count;

  const handleBuildOrEdit = (slot: CurriculumPlan) => {
    dispatch({
      type: 'BUILD_SLOT',
      curriculumId,
      slotId: slot.id,
      curriculumName: curriculum.name,
      sortOrder: slot.sort_order,
      planCount,
      // No date: a slot isn't scheduled. Outcomes/prerequisites come off the
      // curriculum so the builder can show them read-only while the slot is built.
      draft: draftFromSlot(slot, curriculum, '', false),
    });
  };

  const handleView = (slot: CurriculumPlan) => {
    dispatch({ type: 'OPEN_CURRICULUM_SLOT_VIEW', curriculumId, slotId: slot.id });
  };

  return (
    <Stack gap={4}>
      <Flex align='center' justify='space-between' flexWrap='wrap' gap={3}>
        <Box>
          <Text fontSize='h3' fontWeight='700' color='fg.strong' lineHeight='short'>
            {curriculum.name}
          </Text>
          <Text fontSize='sm' color='fg.muted' mt={1}>
            {builtCount} of {planCount} plans complete
          </Text>
        </Box>
        <Flex gap={2} flexWrap='wrap'>
          <Button variant='secondary' onClick={detailsModal.open}>
            <Icon as={PencilIcon} w={4} h={4} />
            Edit details
          </Button>
          <Button variant='dark' onClick={() => dispatch({ type: 'GO_TO', screen: { name: 'curBrowse', intent: 'manage' } })}>
            Done
          </Button>
        </Flex>
      </Flex>

      {/* No progress bar and no outcomes/prerequisites block here: "x of y plans
          built" already carries the progress, and the curriculum-wide text is
          surfaced on the browse card (cur-browse-screen) where it helps choose a
          curriculum. This screen stays a clean slot list. */}
      <Stack gap={3}>
        {slots.map((slot) => (
          <Card.Root key={slot.id}>
            <Card.Body>
              <Flex align='center' justify='space-between' gap={3} flexWrap='wrap'>
                <Flex align='center' gap={3}>
                  <Flex
                    align='center'
                    justify='center'
                    w={8}
                    h={8}
                    borderRadius='full'
                    bg='bg.subtle'
                    fontSize='sm'
                    fontWeight='700'
                    color='fg.muted'
                  >
                    {slot.sort_order}
                  </Flex>
                  <Stack gap={0.5}>
                    <Text fontWeight='600' color='fg.strong'>
                      {slot.name}
                    </Text>
                    <Text fontSize='sm' color='fg.muted'>
                      {slot.is_built
                        ? `${slot.duration_minutes} min · ${slot.sections.length} section${slot.sections.length === 1 ? '' : 's'}`
                        : 'Empty — nothing added yet'}
                    </Text>
                  </Stack>
                </Flex>

                {/* Status chip sits with the actions, not the title — it reads as
                    the state the buttons act on. The badge recipe uppercases, so
                    these labels render as COMPLETE / INCOMPLETE. */}
                <Flex align='center' gap={2}>
                  <Badge variant={slot.is_built ? 'success' : undefined} size='sm'>
                    {slot.is_built ? 'Complete' : 'Incomplete'}
                  </Badge>
                  {slot.is_built && (
                    <Button variant='secondary' size='sm' onClick={() => handleView(slot)}>
                      View
                    </Button>
                  )}
                  <Button
                    variant='outline'
                    colorPalette='brand'
                    size='sm'
                    onClick={() => handleBuildOrEdit(slot)}
                  >
                    {slot.is_built ? 'Edit' : 'Add content'}
                  </Button>
                </Flex>
              </Flex>
            </Card.Body>
          </Card.Root>
        ))}
      </Stack>

      {/* Always mounted and driven by isOpen — conditionally rendering an open
          Chakra dialog skips its close transition and leaves the body scroll /
          pointer lock behind (see commits 9ca4cb5, f35602c). */}
      <CurDetailsModal {...detailsModal.props} curriculum={curriculum} />
    </Stack>
  );
}
